import {
  parseAccessDirectory,
  type AccessChange,
  type UserAccessDirectory,
} from "./user-access-api.ts";
export type ViewerAccountStatus = "pending" | "active" | "disabled";
export type ViewerSetupDeliveryStatus = "not_sent" | "sent" | "failed";

export interface ViewerSummary {
  readonly authUid: string;
  readonly role: "viewer";
  readonly status: ViewerAccountStatus;
  readonly mfaEnrolled: boolean;
  readonly setupDeliveryStatus: ViewerSetupDeliveryStatus;
}

export interface ViewerDirectoryItem extends ViewerSummary {
  readonly email: string;
  readonly lastSignInAt: string | null;
}

export interface AuditEventSummary {
  readonly actorUid: string;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string;
  readonly occurredAt: string;
  readonly outcome: "success" | "denied" | "failed";
}

type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;
const browserFetch: FetchLike = (input, init) => globalThis.fetch(input, init);

export interface AdminUsersApiOptions {
  readonly baseUrl: string;
  readonly getIdToken: (forceRefresh?: boolean) => Promise<string>;
  readonly fetch?: FetchLike;
}

export interface AdminUsersClient {
  listUserAccess?(): Promise<UserAccessDirectory>;
  updateUserAccess?(authUid: string, change: AccessChange): Promise<void>;
  deleteUserAccess?(authUid: string, expectedRevision: number): Promise<void>;
  listViewers(): Promise<readonly ViewerDirectoryItem[]>;
  getViewerProvisioningCapability?(): Promise<ViewerProvisioningCapability>;
  createViewer(email: string): Promise<ViewerCreationResult>;
  disableViewer(authUid: string): Promise<ViewerSummary>;
  resetViewerAccess(authUid: string): Promise<ViewerSummary>;
  resendViewerSetup(authUid: string): Promise<ViewerSetupResult>;
  listAuditEvents(): Promise<readonly AuditEventSummary[]>;
}

export interface ViewerProvisioningCapability {
  readonly canProvisionViewers: boolean;
}

export interface ViewerCreationResult {
  readonly viewer: ViewerSummary;
  readonly outcome:
    "created" | "already_active" | "pending_setup" | "recovered";
}

export interface ViewerSetupResult {
  readonly viewer: ViewerSummary;
  readonly outcome: "setup_sent" | "already_active";
}

export type AdminUsersErrorCode =
  | "protected_account"
  | "access_conflict"
  | "invalid_deletion"
  | "user_not_found"
  | "user_deletion_in_progress"
  | "user_deletion_unavailable"
  | "invalid_email"
  | "invalid_password"
  | "viewer_not_found"
  | "protected_identity"
  | "viewer_identity_conflict"
  | "viewer_profile_conflict"
  | "viewer_disabled"
  | "viewer_provisioning_in_progress"
  | "viewer_setup_delivery_failed"
  | "viewer_identity_permission_denied"
  | "viewer_identity_unavailable"
  | "viewer_profile_unavailable"
  | "viewer_audit_unavailable"
  | "viewer_disable_failed"
  | "viewer_access_reset_failed"
  | "permission_denied"
  | "session_expired"
  | "network_unavailable"
  | "service_unavailable"
  | "invalid_response"
  | "request_failed";

export class AdminUsersApiError extends Error {
  readonly code: AdminUsersErrorCode;
  readonly retryable: boolean;

  constructor(code: AdminUsersErrorCode, retryable = false) {
    super(code);
    this.name = "AdminUsersApiError";
    this.code = code;
    this.retryable = retryable;
  }
}

export const ADMIN_USERS_ERROR_COPY: Readonly<
  Record<AdminUsersErrorCode, string>
> = Object.freeze({
  protected_account: "This protected account cannot be deleted.",
  access_conflict: "The account changed. Refresh the list before retrying.",
  invalid_deletion: "Confirm the user deletion before continuing.",
  user_not_found: "The user was not found. Refresh the list.",
  user_deletion_in_progress: "User deletion is in progress. Retry shortly.",
  user_deletion_unavailable:
    "User deletion did not finish. Retry to complete removal.",
  invalid_email: "Enter a valid viewer email address.",
  invalid_password:
    "Use 14–128 characters with uppercase, lowercase, number, and symbol.",
  viewer_not_found:
    "This viewer account could not be found. Refresh the directory.",
  protected_identity:
    "This protected identity cannot be converted to a viewer.",
  viewer_identity_conflict:
    "This email belongs to an existing sign-in identity and cannot be converted to a viewer.",
  viewer_profile_conflict:
    "This viewer account needs administrator review before setup can continue.",
  viewer_disabled:
    "This viewer is disabled. Restore access before resending setup.",
  viewer_provisioning_in_progress:
    "Viewer setup is already in progress. Refresh the directory and try again shortly.",
  viewer_setup_delivery_failed:
    "Delivery failed — the account is pending. Use Resend setup to try again.",
  viewer_identity_permission_denied:
    "Viewer administration is not configured correctly. Contact the system administrator.",
  viewer_identity_unavailable:
    "The sign-in service is temporarily unavailable. Try again.",
  viewer_profile_unavailable:
    "The viewer directory is temporarily unavailable. Try again.",
  viewer_audit_unavailable:
    "The account changed, but its audit record could not be confirmed. Refresh the directory before retrying.",
  viewer_disable_failed: "The viewer could not be disabled. Try again.",
  viewer_access_reset_failed: "Viewer access could not be restored. Try again.",
  permission_denied: "Administrator access is required to manage viewers.",
  session_expired: "Your secure session expired. Sign in again to continue.",
  network_unavailable: "The service could not be reached. Try again.",
  service_unavailable: "Viewer administration is temporarily unavailable.",
  invalid_response: "Viewer administration returned an invalid response.",
  request_failed: "The request could not be completed. Try again.",
});

export function safeAdminUsersErrorMessage(error: unknown): string {
  return error instanceof AdminUsersApiError
    ? ADMIN_USERS_ERROR_COPY[error.code]
    : ADMIN_USERS_ERROR_COPY.request_failed;
}

const VIEWER_STATUSES = new Set<ViewerAccountStatus>([
  "pending",
  "active",
  "disabled",
]);

const DELIVERY_STATUSES = new Set<ViewerSetupDeliveryStatus>([
  "not_sent",
  "sent",
  "failed",
]);

const CREATION_OUTCOMES = new Set<ViewerCreationResult["outcome"]>([
  "created",
  "already_active",
  "pending_setup",
  "recovered",
]);

const SETUP_OUTCOMES = new Set<ViewerSetupResult["outcome"]>([
  "setup_sent",
  "already_active",
]);

const SERVER_ERROR_CODES: Readonly<Record<string, AdminUsersErrorCode>> =
  Object.freeze({
    protected_account: "protected_account",
    access_conflict: "access_conflict",
    invalid_deletion: "invalid_deletion",
    user_not_found: "user_not_found",
    user_deletion_in_progress: "user_deletion_in_progress",
    user_deletion_unavailable: "user_deletion_unavailable",
    invalid_email: "invalid_email",
    invalid_password: "invalid_password",
    invalid_initial_password: "invalid_password",
    viewer_not_found: "viewer_not_found",
    protected_identity: "protected_identity",
    viewer_identity_conflict: "viewer_identity_conflict",
    viewer_profile_conflict: "viewer_profile_conflict",
    viewer_disabled: "viewer_disabled",
    viewer_provisioning_in_progress: "viewer_provisioning_in_progress",
    viewer_setup_delivery_failed: "viewer_setup_delivery_failed",
    viewer_identity_permission_denied: "viewer_identity_permission_denied",
    viewer_identity_unavailable: "viewer_identity_unavailable",
    viewer_profile_unavailable: "viewer_profile_unavailable",
    viewer_audit_unavailable: "viewer_audit_unavailable",
    viewer_disable_failed: "viewer_disable_failed",
    viewer_access_reset_failed: "viewer_access_reset_failed",
    permission_denied: "permission_denied",
    invalid_token: "session_expired",
    session_expired: "session_expired",
    internal_error: "service_unavailable",
  });

function parseViewerProvisioningCapability(
  value: unknown,
): ViewerProvisioningCapability {
  if (
    !isRecord(value) ||
    Object.keys(value).length !== 1 ||
    typeof value.canProvisionViewers !== "boolean"
  ) {
    throw new AdminUsersApiError("invalid_response");
  }
  return Object.freeze({
    canProvisionViewers: value.canProvisionViewers,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseViewerSummary(value: unknown): ViewerSummary {
  if (
    !isRecord(value) ||
    typeof value.authUid !== "string" ||
    value.authUid.length === 0 ||
    value.role !== "viewer" ||
    typeof value.status !== "string" ||
    !VIEWER_STATUSES.has(value.status as ViewerAccountStatus) ||
    typeof value.mfaEnrolled !== "boolean" ||
    typeof value.setupDeliveryStatus !== "string" ||
    !DELIVERY_STATUSES.has(
      value.setupDeliveryStatus as ViewerSetupDeliveryStatus,
    )
  ) {
    throw new AdminUsersApiError("invalid_response");
  }
  return Object.freeze({
    authUid: value.authUid,
    role: "viewer" as const,
    status: value.status as ViewerAccountStatus,
    mfaEnrolled: value.mfaEnrolled,
    setupDeliveryStatus: value.setupDeliveryStatus as ViewerSetupDeliveryStatus,
  });
}

function parseViewerCreationResult(value: unknown): ViewerCreationResult {
  if (
    !isRecord(value) ||
    typeof value.outcome !== "string" ||
    !CREATION_OUTCOMES.has(value.outcome as ViewerCreationResult["outcome"])
  ) {
    throw new AdminUsersApiError("invalid_response");
  }
  return Object.freeze({
    viewer: parseViewerSummary(value.viewer),
    outcome: value.outcome as ViewerCreationResult["outcome"],
  });
}

function parseViewerSetupResult(value: unknown): ViewerSetupResult {
  if (
    !isRecord(value) ||
    typeof value.outcome !== "string" ||
    !SETUP_OUTCOMES.has(value.outcome as ViewerSetupResult["outcome"])
  ) {
    throw new AdminUsersApiError("invalid_response");
  }
  return Object.freeze({
    viewer: parseViewerSummary(value.viewer),
    outcome: value.outcome as ViewerSetupResult["outcome"],
  });
}

function parseViewerDirectoryItem(value: unknown): ViewerDirectoryItem {
  const viewer = parseViewerSummary(value);
  if (
    !isRecord(value) ||
    typeof value.email !== "string" ||
    (value.lastSignInAt !== null && typeof value.lastSignInAt !== "string")
  ) {
    throw new AdminUsersApiError("invalid_response");
  }
  return Object.freeze({
    ...viewer,
    email: value.email,
    lastSignInAt: value.lastSignInAt as string | null,
  });
}

export class AdminUsersApi implements AdminUsersClient {
  readonly #baseUrl: string;
  readonly #getIdToken: (forceRefresh?: boolean) => Promise<string>;
  readonly #fetch: FetchLike;

  constructor(options: AdminUsersApiOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/$/, "");
    this.#getIdToken = options.getIdToken;
    this.#fetch = options.fetch ?? browserFetch;
  }

  async #request(path: string, init?: RequestInit): Promise<unknown> {
    const send = async (forceRefresh: boolean) => {
      let token: string;
      try {
        token = await this.#getIdToken(forceRefresh);
      } catch {
        throw new AdminUsersApiError("session_expired");
      }
      try {
        return await this.#fetch(`${this.#baseUrl}${path}`, {
          ...init,
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            ...(init?.body ? { "Content-Type": "application/json" } : {}),
          },
        });
      } catch {
        throw new AdminUsersApiError("network_unavailable");
      }
    };
    let response = await send(false);
    if (response.status === 401) response = await send(true);
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new AdminUsersApiError("invalid_response");
    }
    if (!response.ok) {
      const serverCode =
        isRecord(payload) && typeof payload.error === "string"
          ? SERVER_ERROR_CODES[payload.error]
          : undefined;
      throw new AdminUsersApiError(
        response.status === 401
          ? "session_expired"
          : response.status === 403 && !serverCode
            ? "permission_denied"
            : (serverCode ??
              (response.status >= 500
                ? "service_unavailable"
                : "request_failed")),
        isRecord(payload) && payload.retryable === true,
      );
    }
    return payload;
  }

  async getViewerProvisioningCapability(): Promise<ViewerProvisioningCapability> {
    const payload = await this.#request(
      "/v1/admin/viewer-provisioning-capability",
    );
    return parseViewerProvisioningCapability(payload);
  }

  async listUserAccess(): Promise<UserAccessDirectory> {
    return parseAccessDirectory(await this.#request("/v1/admin/user-access"));
  }

  async updateUserAccess(authUid: string, change: AccessChange): Promise<void> {
    const value = await this.#request(
      `/v1/admin/user-access/${encodeURIComponent(authUid)}`,
      { method: "POST", body: JSON.stringify(change) },
    );
    if (!isRecord(value) || value.saved !== true)
      throw new AdminUsersApiError("invalid_response");
  }

  async deleteUserAccess(
    authUid: string,
    expectedRevision: number,
  ): Promise<void> {
    const value = await this.#request(
      `/v1/admin/user-access/${encodeURIComponent(authUid)}/delete`,
      {
        method: "POST",
        body: JSON.stringify({ confirmation: "delete-user", expectedRevision }),
      },
    );
    if (!isRecord(value) || value.deleted !== true)
      throw new AdminUsersApiError("invalid_response");
  }

  async createViewer(email: string): Promise<ViewerCreationResult> {
    const payload = await this.#request("/v1/admin/viewers", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    return parseViewerCreationResult(payload);
  }

  async listViewers(): Promise<readonly ViewerDirectoryItem[]> {
    const payload = await this.#request("/v1/admin/viewers");
    if (!isRecord(payload) || !Array.isArray(payload.viewers)) {
      throw new AdminUsersApiError("invalid_response");
    }
    return Object.freeze(payload.viewers.map(parseViewerDirectoryItem));
  }

  async disableViewer(authUid: string): Promise<ViewerSummary> {
    const payload = await this.#request(
      `/v1/admin/viewers/${encodeURIComponent(authUid)}:disable`,
      { method: "POST" },
    );
    return parseViewerSummary(isRecord(payload) ? payload.viewer : null);
  }

  async resetViewerAccess(authUid: string): Promise<ViewerSummary> {
    const payload = await this.#request(
      `/v1/admin/viewers/${encodeURIComponent(authUid)}:reset-access`,
      { method: "POST" },
    );
    return parseViewerSummary(isRecord(payload) ? payload.viewer : null);
  }

  async resendViewerSetup(authUid: string): Promise<ViewerSetupResult> {
    const payload = await this.#request(
      `/v1/admin/viewers/${encodeURIComponent(authUid)}:resend-setup`,
      { method: "POST" },
    );
    return parseViewerSetupResult(payload);
  }

  async listAuditEvents(): Promise<readonly AuditEventSummary[]> {
    const payload = await this.#request("/v1/admin/audit-events");
    if (!isRecord(payload) || !Array.isArray(payload.events)) {
      throw new AdminUsersApiError("invalid_response");
    }
    return payload.events as readonly AuditEventSummary[];
  }
}
